import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const faqs = [
  {
    question: 'What is Polk Chain?',
    answer: 'Polk Chain is a next-generation Layer 1 blockchain built on Proof of Authority (PoA) consensus. It offers lightning-fast transactions, minimal fees, and enterprise-grade security, making it ideal for DeFi, NFTs, and enterprise applications.',
  },
  {
    question: 'How does Proof of Authority (PoA) work?',
    answer: 'PoA relies on a set of pre-approved, trusted validators who stake their reputation to secure the network. This allows for faster block times and higher throughput while maintaining decentralization through validator diversity and governance.',
  },
  {
    question: 'What is PUSD?',
    answer: 'PUSD is Polk Chain\'s native stablecoin, algorithmically pegged to the US Dollar. It provides a stable medium of exchange within the ecosystem for trading, payments, and DeFi activities without volatility concerns.',
  },
  {
    question: 'How do I use Polk Wallet?',
    answer: 'Download Polk Wallet from the App Store or Google Play, create a new wallet or import an existing one using your seed phrase. You can then store, send, and receive Polk Coin, PUSD, and other tokens on the Polk Chain network.',
  },
  {
    question: 'Is Polk Chain secure?',
    answer: 'Absolutely. Polk Chain employs multiple security layers including smart contract audits by CertiK, a vetted validator system, military-grade encryption, and an active bug bounty program. Our infrastructure is SOC 2 Type II compliant.',
  },
];

const FAQItem = ({ faq, isOpen, onClick, isDark }) => {
  return (
    <div className={`border-b last:border-b-0 ${isDark ? 'border-white/10' : 'border-black/10'}`}>
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left"
        data-testid={`faq-${faq.question.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
      >
        <span className={`text-lg font-semibold pr-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {faq.question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown className={`w-5 h-5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`} />
        </motion.div>
      </button>
      <motion.div
        initial={false}
        animate={{
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className={`pb-6 leading-relaxed ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
          {faq.answer}
        </p>
      </motion.div>
    </div>
  );
};

export const FAQ = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [openIndex, setOpenIndex] = useState(0);
  const { isDark } = useTheme();

  return (
    <section
      id="faq"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#F1F5F9]'}`}
      ref={ref}
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            FAQ
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Everything you need to know about Polk Chain and its ecosystem.
          </p>
        </motion.div>

        {/* FAQ List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`rounded-3xl border p-8 ${
            isDark 
              ? 'bg-white/[0.03] border-white/10' 
              : 'bg-white border-black/5'
          }`}
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={faq.question}
              faq={faq}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              isDark={isDark}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
